// ============================================================
// MOONSHINE MASTERS — Woods3D.jsx        THE BLENDER WOODS (2026-10-24)
//
// The runtime half of gamemaster/models/sculpt/builders/moonshine_blender/ (moonshine_kit.glb):
// the pines, the yard, the dressing of every gathering place and the gather spots themselves.
// The LOOK is Annihilation's Grove kit v2 look, borrowed whole: colours painted + AO-baked into the
// model ('Col'), a 4-step toon ramp, and an inverted-hull INK line on wood and stone.
//
//   logic -> data <- renderer: WHERE things stand is worldgen.js (DRESS, pure + seeded); WHICH model a
//   gather spot wears is moonshineData.js (KIT). This file only draws. It never decides a rule.
//
// PERF LAW: everything is instanced and built ONCE; the distance swap (close/mid/far model) is plain number
//   copying when you have walked KIT.LOD.STEP metres - no React, no state in a frame.
// FAIL-SAFE LAW: useKit() is null until the glb arrives (or forever, if it is missing) - World3D keeps
//   drawing its old placeholder woods until then, so the game is always playable.
// ============================================================
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/examples/jsm/libs/meshopt_decoder.module.js";
import { KIT, INGREDIENTS, TUNING, RIG_MODEL, LAYOUT } from "./moonshineData";
import { GEN, DRESS } from "./worldgen";
import { isNight } from "./moonshine";

const BASE = process.env.PUBLIC_URL || "";
export const KIT_URL = `${BASE}/models/moonshine_kit.glb`;

// ── load once; null until it lands (or forever if it is missing) ───────────
let KITG = null, KITP = null;
function loadKit() {
  if (!KITP) KITP = new Promise((res) => {
    const L = new GLTFLoader(); L.setMeshoptDecoder(MeshoptDecoder);
    L.load(KIT_URL, (g) => { KITG = g; res(g); }, undefined, (e) => { console.warn("[moonshine] moonshine_kit.glb missing - the placeholder woods stand in", e); res(null); });
  });
  return KITP;
}
export function useKit() {
  const [k, setK] = useState(KITG);
  useEffect(() => { if (!KITG) { let live = true; loadKit().then((g) => live && g && setK(g)); return () => { live = false; }; } }, []);
  return k;
}

// ── materials: the Grove's toon + ink (Annihilation GroveWorld3D.jsx), plus Moonshine's three ─────
const RAMP = (() => {
  const t = new THREE.DataTexture(new Uint8Array([84, 140, 200, 255]), 4, 1, THREE.RedFormat);
  t.minFilter = t.magFilter = THREE.NearestFilter; t.needsUpdate = true; return t;
})();
const WIND = { value: 0 };
const INK = 0x161114;
const toon = (o = {}) => new THREE.MeshToonMaterial({ gradientMap: RAMP, vertexColors: true, ...o });
// THE SIGHT-LINE FADE: anything closer to the camera than FADE.FAR, or standing inside the tube from the camera
// to the player (radius FADE.TUBE), dissolves with a screen-door dither - so a giant pine between the follow cam
// and you never hides you. Every kit material wears it. PLAYER is written once a frame by <Woods/> (a ref, no React).
const FADE = { NEAR: 1.6, FAR: 3.8, TUBE: 2.4, SOFT: 1.0 };
export const PLAYER = { value: new THREE.Vector3(0, -999, 0) };
function nearFade(m, tag) {
  const prev = m.onBeforeCompile;
  m.onBeforeCompile = (sh, r) => {
    if (prev) prev.call(m, sh, r);
    sh.uniforms.uMmPlayer = PLAYER;
    sh.vertexShader = sh.vertexShader
      .replace("#include <common>", "#include <common>\nvarying float vMmDepth;\nvarying vec3 vMmWorld;")
      .replace("#include <project_vertex>", `#include <project_vertex>
        vMmDepth = -mvPosition.z;
        { vec4 w = vec4(transformed, 1.0);
          #ifdef USE_INSTANCING
            w = instanceMatrix * w;
          #endif
          vMmWorld = (modelMatrix * w).xyz; }`);
    sh.fragmentShader = sh.fragmentShader
      .replace("#include <common>", "#include <common>\nvarying float vMmDepth;\nvarying vec3 vMmWorld;\nuniform vec3 uMmPlayer;")
      .replace("void main() {", `void main() {
        { float keep = clamp((vMmDepth - ${FADE.NEAR.toFixed(2)}) / ${(FADE.FAR - FADE.NEAR).toFixed(2)}, 0.0, 1.0);
          vec3 ab = uMmPlayer - cameraPosition; float L = length(ab);
          if (L > 0.5) {
            vec3 dir = ab / L; vec3 ap = vMmWorld - cameraPosition; float t = dot(ap, dir);
            if (t > 0.0 && t < L - 0.7) keep = min(keep, smoothstep(${(FADE.TUBE - FADE.SOFT).toFixed(2)}, ${FADE.TUBE.toFixed(2)}, length(ap - dir * t)));
          }
          vec2 q = mod(floor(gl_FragCoord.xy), 4.0);
          float th = (mod(q.x + q.y * 2.0, 4.0) * 4.0 + mod(q.y + q.x * 3.0, 4.0)) / 16.0;   // 4x4 ordered dither
          if (keep < 1.0 && keep <= th) discard; }`);
  };
  const key = m.customProgramCacheKey ? m.customProgramCacheKey.bind(m) : () => "";
  m.customProgramCacheKey = () => key() + "_mmfade_" + tag;   // tag: the leaf (wind) and plain toon must NOT share a program
  return m;
}
let MATS = null;
function mats() {
  if (MATS) return MATS;
  const leaf = toon({ side: THREE.DoubleSide });
  leaf.onBeforeCompile = (sh) => {
    sh.uniforms.uTime = WIND;
    sh.vertexShader = sh.vertexShader
      .replace("#include <common>", "#include <common>\nuniform float uTime;")
      .replace("#include <begin_vertex>", `#include <begin_vertex>
        #ifdef USE_INSTANCING
          float ph = instanceMatrix[3].x * 0.11 + instanceMatrix[3].z * 0.09;
        #else
          float ph = 0.0;
        #endif
        float sw = max(transformed.y, 0.0);
        transformed.x += sin(uTime * 1.4 + ph) * 0.028 * sw * (1.0 / (1.0 + sw * 0.2));
        transformed.z += cos(uTime * 1.1 + ph) * 0.020 * sw * (1.0 / (1.0 + sw * 0.2));`);
  };
  MATS = {
    grove_wood: toon(), grove_stone: toon(), grove_leaf: leaf,
    mm_fruit: toon({ side: THREE.DoubleSide }),          // single cards: papers, husk peels
    glow_shroom: new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: false, side: THREE.DoubleSide }),   // petals are single cards
    mm_glass: toon({ transparent: true, opacity: 0.55, depthWrite: false }),
    mm_water: new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.82, depthWrite: false }),
  };
  for (const [k, m] of Object.entries(MATS)) nearFade(m, k);
  return MATS;
}
// ink width per kind (metres pushed along the normal): a thin stem with a 4 cm halo looks like a blob
const INK_CACHE = {};
function inkMat(w) {
  if (INK_CACHE[w]) return INK_CACHE[w];
  const m = new THREE.MeshBasicMaterial({ color: INK, side: THREE.BackSide });
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uInk = { value: w };
    sh.vertexShader = sh.vertexShader
      .replace("#include <common>", "#include <common>\nuniform float uInk;")
      .replace("#include <begin_vertex>", "vec3 transformed = position + normal * uInk;");
  };
  m.customProgramCacheKey = () => "mm_ink_" + w;
  nearFade(m, "ink" + w);
  return (INK_CACHE[w] = m);
}
const NO_INK = new Set(["fern", "tuft", "reed", "mushroom", "flower", "herb_node", "herb_b", "moonbloom_open", "moonbloom_bud", "anima_bloom",
  "corn_a", "corn_b", "corn_node", "pumpkin_vine", "berry_red", "berry_blue", "bush"]);
const inkWidth = (kind) => NO_INK.has(kind) ? 0
  : kind.startsWith("pine") ? 0.05
  : kind === "rock" || kind === "rubble" || kind.startsWith("ruin") || kind === "stump" || kind === "log" ? 0.035
  : kind === "lantern" ? 0.008
  : kind === "still" || kind.startsWith("rig_") || kind === "jar_crate" || kind === "jug" || kind === "scrap_pile" ? 0.012
  : 0.02;

// the kit's masters: node name -> [mesh parts], one part per material
function mastersOf(g) {
  const m = {};
  g.scene.traverse((o) => {
    if (!o.isMesh) return;
    let n = o; while (n.parent && n.parent !== g.scene) n = n.parent;
    (m[n.name] ||= []).push(o);
  });
  return m;
}
const levelsOf = (M, kind) => {
  const l0 = M[kind]; if (!l0) return null;
  const out = [l0]; if (M[kind + "_lod1"]) out.push(M[kind + "_lod1"]); if (M[kind + "_lod2"]) out.push(M[kind + "_lod2"]);
  return out;
};
const TINTS = Object.fromEntries(Object.entries(KIT.LEAF_TINT).map(([k, a]) => [k, a.map((h) => new THREE.Color(h))]));

// ── THE WOODS: every DRESS row, instanced, with the close/mid/far swap ───────
function buildWoods(gltf) {
  const M = mastersOf(gltf), Mt = mats();
  const root = new THREE.Group(); root.name = "moonshine_woods";
  const byKind = {};
  for (const row of DRESS) (byKind[row[0]] ||= []).push(row);
  const missing = [];
  const kinds = [];
  const tmp = new THREE.Object3D(), col = new THREE.Color();
  for (const [kind, rows] of Object.entries(byKind)) {
    const levels = levelsOf(M, kind);
    if (!levels) { missing.push(kind); continue; }
    const n = rows.length, w = inkWidth(kind);
    // per instance: its matrix (16 floats) and its leaf tint (3 floats), computed ONCE
    const mtx = new Float32Array(n * 16), tint = new Float32Array(n * 3), X = new Float32Array(n), Z = new Float32Array(n);
    rows.forEach(([, x, z, ry, s, t], i) => {
      tmp.position.set(x, 0, z); tmp.rotation.set(0, ry, 0); tmp.scale.setScalar(s); tmp.updateMatrix();
      tmp.matrix.toArray(mtx, i * 16); X[i] = x; Z[i] = z;
      const pal = TINTS[kind];
      if (pal) col.copy(pal[t % pal.length]);
      else { const k = 0.9 + ((t * 37) % 19) / 100; col.setRGB(k, k * (0.98 + ((t * 13) % 7) / 200), k); }   // painted leaves: a gentle nudge only
      tint[i * 3] = col.r; tint[i * 3 + 1] = col.g; tint[i * 3 + 2] = col.b;
    });
    const lv = levels.map((parts, lvl) => parts.map((part) => {
      const mn = part.material?.name || "grove_wood";
      const im = new THREE.InstancedMesh(part.geometry, Mt[mn] || Mt.grove_wood, n);
      im.frustumCulled = false; im.count = 0; im.name = kind;
      if (mn === "grove_leaf") im.setColorAt(0, col.setRGB(1, 1, 1));
      root.add(im);
      let hull = null;
      if (w && lvl < 2 && (mn === "grove_wood" || mn === "grove_stone")) {   // no ink on the far models: a pixel-wide line in the fog
        hull = new THREE.InstancedMesh(part.geometry, inkMat(w), n);
        hull.frustumCulled = false; hull.count = 0; hull.instanceMatrix = im.instanceMatrix; root.add(hull);
      }
      return { im, hull, leaf: mn === "grove_leaf" };
    }));
    kinds.push({ kind, n, mtx, tint, X, Z, lv, static: lv.length === 1 });
  }
  if (missing.length) console.warn("[moonshine] kit has no master for:", missing.join(", "));
  // the swap: which level each instance wears, from the player's position
  const N2 = KIT.LOD.NEAR ** 2, M2 = KIT.LOD.MID ** 2;
  const fill = (K, px, pz) => {
    const cnt = K.lv.map(() => 0);
    for (let i = 0; i < K.n; i++) {
      let l = 0;
      if (!K.static) { const dx = K.X[i] - px, dz = K.Z[i] - pz, d2 = dx * dx + dz * dz; l = d2 < N2 ? 0 : d2 < M2 ? 1 : 2; if (l >= K.lv.length) l = K.lv.length - 1; }
      const c = cnt[l]++;
      for (const p of K.lv[l]) {
        p.im.instanceMatrix.array.set(K.mtx.subarray(i * 16, i * 16 + 16), c * 16);
        if (p.leaf) p.im.instanceColor.array.set(K.tint.subarray(i * 3, i * 3 + 3), c * 3);
      }
    }
    K.lv.forEach((parts, l) => parts.forEach((p) => {
      p.im.count = cnt[l]; if (p.hull) p.hull.count = cnt[l];
      p.im.instanceMatrix.needsUpdate = true; if (p.leaf) p.im.instanceColor.needsUpdate = true;
    }));
  };
  let lx = 1e9, lz = 1e9, first = true;
  const update = (px, pz) => {
    if (!first && Math.hypot(px - lx, pz - lz) < KIT.LOD.STEP) return;
    for (const K of kinds) if (first || !K.static) fill(K, px, pz);
    first = false; lx = px; lz = pz;
  };
  return { root, update, total: DRESS.length, masters: M };
}

export const Woods = memo(function Woods({ kit, posRef }) {
  const built = useMemo(() => buildWoods(kit), [kit]);
  useEffect(() => {
    const p = posRef.current; built.update(p.x, p.z);
    if (process.env.NODE_ENV !== "production") console.info(`[moonshine] woods: ${built.total} props from moonshine_kit.glb`);
  }, [built, posRef]);
  // THE LIVE CAMP: a bigger camp remounts this (World3D's key) and builds a fresh set - free the old instance buffers on the GPU
  // (the kit's geometries and the shared materials stay; InstancedMesh.dispose only lets go of its own per-instance data)
  useEffect(() => () => built.root.traverse((o) => { if (o.isInstancedMesh) o.dispose(); }), [built]);
  useFrame((s) => { WIND.value = s.clock.elapsedTime; const p = posRef.current; if (p) { built.update(p.x, p.z); PLAYER.value.set(p.x, p.y + 1.1, p.z); } });
  return <primitive object={built.root} />;
});

// ── one kit model as an ordinary (non-instanced) group: the gather spots use these ──
function modelGroup(M, kind) {
  const parts = M[kind]; if (!parts) return null;
  const Mt = mats(), w = inkWidth(kind), g = new THREE.Group(); g.name = kind;
  for (const part of parts) {
    const mn = part.material?.name || "grove_wood";
    g.add(new THREE.Mesh(part.geometry, Mt[mn] || Mt.grove_wood));
    if (w && (mn === "grove_wood" || mn === "grove_stone")) g.add(new THREE.Mesh(part.geometry, inkMat(w)));
  }
  return g;
}

// ── THE GATHER SPOTS: the real plant stands there; picked = it shrinks away and grows back.
// A gather spot (and NOTHING else) wears the cue: a soft bobbing sparkle + a faint ring in its ingredient's colour.
export const GatherSpots = memo(function GatherSpots({ kit, WILD }) {
  const M = useMemo(() => mastersOf(kit), [kit]);
  const spots = useMemo(() => GEN.nodes.map((n, i) => {
    const g = new THREE.Group(); g.position.set(n.x, 0, n.z); g.rotation.y = (i * 2.399) % 6.283;
    const main = modelGroup(M, KIT.NODE_MODEL[n.ing]); if (main) g.add(main);
    const sleepName = KIT.NODE_SLEEP_MODEL[n.ing];
    const sleep = n.night && sleepName ? modelGroup(M, sleepName) : null; if (sleep) g.add(sleep);
    const box = new THREE.Box3().setFromObject(main || g);
    const h = Math.min(1.7, Math.max(0.55, (isFinite(box.max.y) ? box.max.y : 0.5) + 0.3));   // the cue floats just over the plant
    return { n, g, main, sleep, h };
  }), [M]);
  const cue = useRef([]);
  useFrame(({ clock }) => {
    const now = Date.now(), night = isNight(now), t = clock.elapsedTime;
    spots.forEach((S, i) => {
      const at = WILD.pickedAt[S.n.id];
      const avail = !(at && now - at < TUNING.NODE_RESPAWN_S * 1000);
      const open = avail && (!S.n.night || night) && (!S.n.eyes || WILD.eyes);   // a hidden (tier-3) patch shows only through the Eyes
      const asleep = avail && S.n.night && !night;
      const ease = (o, target) => { if (!o) return; const s = o.scale.x + (target - o.scale.x) * 0.12; o.scale.setScalar(Math.max(0.0001, s)); o.visible = s > 0.002; };
      ease(S.main, open ? 1 : 0);
      ease(S.sleep, asleep ? 1 : 0);
      const c = cue.current[i];
      if (c) {
        c.visible = open;
        const sp = c.children[0]; sp.position.y = S.h + Math.sin(t * 2.2 + i) * 0.1; sp.rotation.y = t * 1.6 + i;
        sp.material.opacity = 0.55 + 0.35 * Math.sin(t * 3.1 + i * 1.7);
      }
    });
  });
  return (
    <group>
      {spots.map((S, i) => {
        const ing = INGREDIENTS[S.n.ing];
        return (
          <group key={S.n.id}>
            <primitive object={S.g} />
            <group ref={(el) => { cue.current[i] = el; }} position={[S.n.x, 0, S.n.z]}>
              <mesh><octahedronGeometry args={[0.07, 0]} /><meshBasicMaterial color={KIT.GATHER_CUE} transparent opacity={0.8} toneMapped={false} depthWrite={false} /></mesh>
              <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.03, 0]}><ringGeometry args={[0.55, 0.68, 28]} /><meshBasicMaterial color={ing.color} transparent opacity={0.32} depthWrite={false} /></mesh>
            </group>
          </group>
        );
      })}
    </group>
  );
});

// ── THE STILL, LIVE (2026-10-24): built from the parts you own (mm_rig.py's modular masters, fixed joints), so buying a
// Thumper Keg or bolting on the junk radiator changes the still in the yard. No rig masters in the kit -> the old
// single "still" master stands in (and World3D's primitives stand in for the parts). Rebuilt only when the rig changes.
const RIGKIT = new WeakMap();
export const hasRigKit = (kit) => { if (!kit) return false; if (!RIGKIT.has(kit)) RIGKIT.set(kit, !!mastersOf(kit)[RIG_MODEL.base]); return RIGKIT.get(kit); };
// at = where it stands (default the first still; THE SECOND STILL passes LAYOUT.still2 and its OWN rig2, 2026-10-31). seized = the
// revenuers hauled it off: hidden (kept built, so getting it back costs nothing).
export const StillRig = memo(function StillRig({ kit, rig, at = LAYOUT.still, seized = false }) {
  const M = useMemo(() => mastersOf(kit), [kit]);
  const key = rig ? `${rig.head}|${rig.thumper}|${rig.cond}` : "";
  const g = useMemo(() => {
    const root = new THREE.Group(); root.name = "still_rig"; root.position.set(at[0], 0, at[1]);
    const r = rig || {};
    const names = M[RIG_MODEL.base]
      ? [RIG_MODEL.base, r.head === null ? null : RIG_MODEL.head[r.head] || RIG_MODEL.head.pot, r.head === null || r.cond === null ? null : RIG_MODEL.thumper[r.thumper] || RIG_MODEL.thumper.none, r.cond === null ? null : RIG_MODEL.cond[r.cond] || RIG_MODEL.cond.worm].filter(Boolean)   // still 2 starts bare (null = not bought yet)
      : ["still"];
    for (const n of names) { const m = modelGroup(M, n); if (m) root.add(m); }
    return root;
  }, [M, key, at[0], at[1]]);   // eslint-disable-line react-hooks/exhaustive-deps -- `key` IS the rig
  return <primitive object={g} visible={!seized} />;
});
